import React from "react";
import { HashRouter, useLocation } from "react-router";
import AppRoutes from "./routes/routes";
import CookieBanner from "./components/Layout/CookieBanner/CookieBanner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import useScrollToTop from "./hooks/useScrollToTop";
import { clearApiBaseCache, warmupApi } from "./config/api";

const queryClient = new QueryClient();
const HOME_CACHE_CLEAR_VERSION = "2026-06-02-4";

const ScrollToTopOnRouteChange = () => {
  useScrollToTop();
  return null;
};


const ApiWarmup: React.FC = () => {
  React.useEffect(() => {
    warmupApi();

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        warmupApi();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  return null;
};

const HomeCacheMaintenance: React.FC = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    if (pathname !== "/") return;

    const markerKey = "home_cache_clear_version";
    const alreadyCleared = localStorage.getItem(markerKey) === HOME_CACHE_CLEAR_VERSION;
    if (alreadyCleared) return;

    let isCancelled = false;

    const clearBrowserCaches = async () => {
      try {
        clearApiBaseCache();
        if ("caches" in window) {
          const cacheNames = await caches.keys();
          await Promise.all(cacheNames.map((name) => caches.delete(name)));
        }

        if ("serviceWorker" in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map((registration) => registration.unregister()));
        }
      } catch (err) {
        console.warn("Cache cleanup failed:", err);
      } finally {
        if (isCancelled) return;
        localStorage.setItem(markerKey, HOME_CACHE_CLEAR_VERSION);

        window.location.reload();
      }
    };

    clearBrowserCaches();

    return () => {
      isCancelled = true;
    };
  }, [pathname]);

  return null;
};

const App: React.FC = () => {
  return (
    <HashRouter>
      <QueryClientProvider client={queryClient}>
        <ApiWarmup />
        <HomeCacheMaintenance />
        <ScrollToTopOnRouteChange />
        <AppRoutes />
        <CookieBanner />
      </QueryClientProvider>
    </HashRouter>
  );
};

export default App;
